import { createContext, useContext, useEffect, useState } from "react";
import { toast } from "react-toastify";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if JWT cookie exists
    const tokenExists = document.cookie.includes("jwt");

    if (!tokenExists) {
      setLoading(false); // No user logged in, stop loading
      return;
    }
    const fetchUser = async () => {
      setLoading(true);
      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/api/users/profile`,
          { credentials: "include" },
        );

        if (res.status === 401) {
          // Token missing or invalid → no user
          toast.error("Please log in!");
          setUser(null);
          setLoading(false);
          return;
        }

        if (!res.ok) {
          console.error("Failed to fetch user profile", res.status);
          setUser(null);
          setLoading(false);
          return;
        }

        const data = await res.json();
        setUser(data);
      } catch (err) {
        console.error(`Error fetching user profile:, ${err.message}`);
        toast.error(`Error fetching user profile:, ${err.message}`);
        setUser(null);
      } finally {
        setLoading(false);
      }
    };
    fetchUser();
  }, []);

  const logout = async () => {
    try {
      await fetch(`${process.env.REACT_APP_API_URL}/api/users/logout`, {
        method: "POST",
        credentials: "include",
      });
      toast.success("Logout was successfull!");
    } catch (err) {
      toast.error(err.message || "Failed to log out. Please try again later");
    } finally {
      setUser(null);
    }
  };

  const refreshUser = async () => {
    const res = await fetch(
      `${process.env.REACT_APP_API_URL}/api/users/profile`,
      {
        credentials: "include",
      },
    );

    if (!res.ok) {
      setUser(null);
      return;
    }

    const data = await res.json();
    setUser(data);
  };

  return (
    <AuthContext.Provider
      value={{ user, setUser, logout, loading, refreshUser }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used inside AuthProvider");
  }
  return context;
};
